import { useEffect, useRef, useState } from "react";
import { Eraser, Check, PenTool, Type } from "lucide-react";

/**
 * Signature pad with two modes:
 *   • DRAW — canvas mouse/touch strokes (mobile / tablet friendly)
 *   • TYPE — type your name; renders in a script font onto a canvas.
 *
 * Both modes emit the same `data:image/png;base64,…` payload via onChange,
 * so every downstream consumer (backend, PDF rasteriser, existing signatures
 * panel) works unchanged.
 */
export default function SignaturePad({
  onChange,
  label = "Signature",
  testid = "signature-pad",
  height = 180,
}) {
  const [mode, setMode] = useState("draw"); // 'draw' | 'type'
  const [typed, setTyped] = useState("");
  const [typeFont, setTypeFont] = useState("Homemade Apple"); // vs "Caveat"
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(true);

  // ---------- CANVAS SETUP ----------
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const resize = () => {
      const parent = c.parentElement;
      const w = parent.clientWidth;
      const h = height;
      const dpr = window.devicePixelRatio || 1;
      c.width = w * dpr;
      c.height = h * dpr;
      c.style.width = w + "px";
      c.style.height = h + "px";
      const ctx = c.getContext("2d");
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#f5f5f5";
      // If we're in type mode, re-render the typed signature after resize.
      if (mode === "type") renderTyped();
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // renderTyped closes over the latest state via ref-like pattern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, mode, typeFont]);

  // Re-render typed signature on text / font change
  useEffect(() => {
    if (mode !== "type") return;
    renderTyped();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed, typeFont, mode]);

  // ---------- DRAW MODE ----------
  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = (e) => {
    if (mode !== "draw") return;
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };
  const move = (e) => {
    if (mode !== "draw") return;
    if (!drawing.current) return;
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#f5f5f5";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (empty) setEmpty(false);
  };
  const end = () => {
    if (mode !== "draw") return;
    if (!drawing.current) return;
    drawing.current = false;
    emit();
  };

  // ---------- TYPE MODE ----------
  const renderTyped = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const w = c.width / dpr;
    const h = c.height / dpr;
    ctx.clearRect(0, 0, w, h);

    const text = (typed || "").trim();
    if (!text) {
      setEmpty(true);
      onChange?.(null);
      return;
    }

    // Center the text vertically; scale font size to fit width.
    let fontSize = Math.min(72, Math.floor(h * 0.55));
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillStyle = "#f5f5f5";

    const setFont = (px) => {
      ctx.font = `${px}px "${typeFont}", "Segoe Script", "Brush Script MT", cursive`;
    };
    setFont(fontSize);
    while (ctx.measureText(text).width > w - 40 && fontSize > 18) {
      fontSize -= 2;
      setFont(fontSize);
    }
    ctx.fillText(text, w / 2, h / 2 + 4);

    // Faint underline stroke to match a real signature.
    ctx.strokeStyle = "rgba(245,245,245,0.35)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, h - 14);
    ctx.lineTo(w - 20, h - 14);
    ctx.stroke();

    setEmpty(false);
    emit();
  };

  // ---------- SHARED ----------
  const emit = () => {
    const c = canvasRef.current;
    if (!c) return;
    // If the canvas is genuinely blank, emit null.
    if (isCanvasBlank(c)) {
      onChange?.(null);
      return;
    }
    onChange?.(c.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setEmpty(true);
    setTyped("");
    onChange?.(null);
  };

  const switchMode = (next) => {
    if (next === mode) return;
    // Wipe any current signature when switching modes so the user has a
    // clean slate — prevents accidentally submitting a leftover blob.
    clear();
    setMode(next);
  };

  const disabledEnd = mode === "draw" ? end : undefined;

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest font-black text-muted">
          {label}
        </div>
        <div className="flex items-center gap-1">
          {/* DRAW / TYPE toggle */}
          <div
            className="inline-flex rounded-lg overflow-hidden"
            style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
            role="tablist"
            data-testid="signature-mode-toggle"
          >
            <button
              type="button"
              onClick={() => switchMode("draw")}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] uppercase font-black tracking-wider transition ${
                mode === "draw" ? "text-black" : "text-muted hover:text-white"
              }`}
              style={mode === "draw" ? { background: "var(--primary)" } : {}}
              data-testid="signature-mode-draw"
            >
              <PenTool className="w-3 h-3" /> Draw
            </button>
            <button
              type="button"
              onClick={() => switchMode("type")}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] uppercase font-black tracking-wider transition ${
                mode === "type" ? "text-black" : "text-muted hover:text-white"
              }`}
              style={mode === "type" ? { background: "var(--primary)" } : {}}
              data-testid="signature-mode-type"
            >
              <Type className="w-3 h-3" /> Type
            </button>
          </div>
          <button
            type="button"
            onClick={clear}
            className="text-[11px] uppercase tracking-widest flex items-center gap-1 text-muted hover:text-white px-2 py-1"
            data-testid="signature-clear"
          >
            <Eraser className="w-3.5 h-3.5" /> Clear
          </button>
        </div>
      </div>

      {/* TYPE input row */}
      {mode === "type" && (
        <div className="flex items-center gap-2">
          <input
            className="input !min-h-[40px] !py-1"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Type your full name"
            autoFocus
            maxLength={64}
            data-testid="signature-type-input"
          />
          <select
            className="select !min-h-[40px] !py-1 !w-40"
            value={typeFont}
            onChange={(e) => setTypeFont(e.target.value)}
            data-testid="signature-type-font"
            title="Signature style"
          >
            <option value="Homemade Apple">Handwriting</option>
            <option value="Caveat">Signature</option>
          </select>
        </div>
      )}

      {/* Canvas — used by BOTH modes so the export shape is identical */}
      <div
        className="relative rounded-xl overflow-hidden"
        style={{
          background: "var(--surface)",
          border: "1px dashed var(--border)",
        }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={disabledEnd}
          onMouseLeave={disabledEnd}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={disabledEnd}
          className={`block ${mode === "draw" ? "cursor-crosshair" : "cursor-not-allowed"}`}
          data-testid="signature-canvas"
        />
        {empty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-muted text-sm font-mono-tight">
            {mode === "draw" ? "Sign here" : "Type your name above"}
          </div>
        )}
        <div
          className="absolute bottom-2 left-3 right-3 h-px"
          style={{ background: "var(--border)" }}
        />
      </div>
      {!empty && (
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest" style={{ color: "var(--primary)" }}>
          <Check className="w-3.5 h-3.5" /> Captured
          <span className="ml-2 text-muted normal-case tracking-normal">
            {mode === "type" ? "· typed signature" : "· drawn signature"}
          </span>
        </div>
      )}
    </div>
  );
}

// True if the canvas has no non-transparent pixels.
function isCanvasBlank(canvas) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  if (!width || !height) return true;
  try {
    const data = ctx.getImageData(0, 0, width, height).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] !== 0) return false;
    }
    return true;
  } catch {
    return false;
  }
}
